/**
 * ConsentPreferencesPanel — Per-category cookie preferences
 *
 * Layer 2 of the two-layer consent UI (CookieYes/OneTrust pattern).
 * Four category rows with toggles. Strictly Necessary is always ON and locked.
 * Analytics, Functional, and Marketing default to OFF (GDPR compliant).
 *
 * Toggle states use colour + position + label (not colour alone — WCAG).
 * Panel manages its own local toggle state until Save Preferences is clicked.
 *
 * @epic MVP 0.1.1.2 — Cookie Consent & Preference Layer
 * @story SCRUM-1446
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Lock } from 'lucide-react';
import useConsentPreference from '../../hooks/useConsentPreference';

const CATEGORIES = [
  {
    key: 'necessary',
    label: 'Strictly Necessary',
    description: 'Required for the site to function. Cannot be disabled.',
    locked: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Helps us understand how visitors use the site (Google Analytics).',
    locked: false,
  },
  {
    key: 'functional',
    label: 'Functional',
    description: 'Remembers your preferences to improve your experience.',
    locked: false,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'Used to show relevant content and measure campaign effectiveness.',
    locked: false,
  },
];

const ConsentPreferencesPanel = () => {
  const { consent, isPanelOpen, closePrefPanel, setConsent, acceptAll, rejectAll } = useConsentPreference();

  // Local toggle state — initialises from current consent context
  const [localState, setLocalState] = useState({
    analytics: false,
    functional: false,
    marketing: false,
  });

  // Re-initialise local state when panel opens
  useEffect(() => {
    if (isPanelOpen && consent) {
      setLocalState({
        analytics: consent.analytics ?? false,
        functional: consent.functional ?? false,
        marketing: consent.marketing ?? false,
      });
    } else if (isPanelOpen && !consent) {
      setLocalState({ analytics: false, functional: false, marketing: false });
    }
  }, [isPanelOpen, consent]);

  const handleToggle = useCallback((key) => {
    setLocalState((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleSavePreferences = useCallback(() => {
    setConsent({
      analytics: localState.analytics,
      functional: localState.functional,
      marketing: localState.marketing,
    });
  }, [setConsent, localState]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closePrefPanel();
    }
  }, [closePrefPanel]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      closePrefPanel();
    }
  }, [closePrefPanel]);

  if (!isPanelOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label="Cookie preferences"
        aria-modal="true"
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Cookie Preferences</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage your cookie preferences below.{' '}
            <a
              href="/privacy"
              className="text-emerald-600 hover:text-emerald-700 underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Category rows */}
        <div className="px-6 py-4 space-y-4">
          {CATEGORIES.map((category) => {
            const isOn = category.locked ? true : localState[category.key];
            const toggleId = `consent-toggle-${category.key}`;

            return (
              <div
                key={category.key}
                className="flex items-start justify-between gap-4 py-2"
              >
                <div className="flex-1 min-w-0">
                  <label
                    htmlFor={toggleId}
                    className="text-sm font-medium text-gray-900"
                  >
                    {category.label}
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">{category.description}</p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                  {category.locked ? (
                    // Strictly Necessary — locked ON
                    <div className="flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                      <span className="text-xs text-gray-400 font-medium">Always active</span>
                      <button
                        id={toggleId}
                        type="button"
                        role="switch"
                        aria-checked="true"
                        aria-label={`${category.label}: Always active`}
                        disabled
                        className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 cursor-not-allowed opacity-60"
                      >
                        <span className="inline-block h-4 w-4 rounded-full bg-white shadow translate-x-6 transition-transform" />
                      </button>
                    </div>
                  ) : (
                    // Interactive toggle
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500 font-medium w-6 text-right">
                        {isOn ? 'ON' : 'OFF'}
                      </span>
                      <button
                        id={toggleId}
                        type="button"
                        role="switch"
                        aria-checked={isOn}
                        aria-label={`${category.label}: ${isOn ? 'ON' : 'OFF'}`}
                        onClick={() => handleToggle(category.key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                          isOn ? 'bg-emerald-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            isOn ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={rejectAll}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Reject All
          </button>
          <button
            type="button"
            onClick={handleSavePreferences}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Save Preferences
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentPreferencesPanel;
